import { Amplify } from 'aws-amplify';

// Import test for debugging
import './cognitoConfigTest';

// Environment variables with validation - robust browser access
const getEnvVar = (key, required = true) => {
  try {
    // Try multiple ways to access environment variables in the browser
    let value = null;
    
    // Method 1: Standard process.env (webpack DefinePlugin)
    if (typeof process !== 'undefined' && process.env) {
      value = process.env[key];
    }
    
    // Method 2: Check window.env as fallback
    if (!value && typeof window !== 'undefined' && window.env) {
      value = window.env[key];
    }
    
    // Method 3: Check global environment object
    if (!value && typeof global !== 'undefined' && global.env) {
      value = global.env[key];
    }
    
    if (required && !value) {
      console.error(`❌ Required environment variable ${key} is not set`);
      console.error(`   Available methods: process.env, window.env, global.env`);
      return null;
    }
    return value || '';
  } catch (e) {
    console.error(`❌ Error accessing environment variable ${key}:`, e);
    return null;
  }
};

// Validate required Cognito environment variables
console.log('🔍 Loading Cognito environment variables...');
let userPoolId = getEnvVar('REACT_APP_COGNITO_USER_POOL_ID', false);
let userPoolClientId = getEnvVar('REACT_APP_COGNITO_CLIENT_ID', false);
let cognitoDomain = getEnvVar('REACT_APP_COGNITO_DOMAIN', false);

console.log('🔍 Cognito environment variables loaded:', {
  userPoolId: userPoolId ? `${userPoolId.substring(0, 10)}...` : 'MISSING',
  userPoolClientId: userPoolClientId ? `${userPoolClientId.substring(0, 10)}...` : 'MISSING',
  cognitoDomain: cognitoDomain ? `${cognitoDomain.substring(0, 30)}...` : 'MISSING'
});

// Fallback to hardcoded values if environment variables are not available
if (!userPoolId || !userPoolClientId || !cognitoDomain) {
  console.warn('⚠️ Environment variables not available, using fallback configuration');
  userPoolId = userPoolId || 'eu-west-1_XTXemZYOB';
  userPoolClientId = userPoolClientId || '10198m3o5no6869nupme2hohrb';
  cognitoDomain = cognitoDomain || 'yatabase-main.auth.eu-west-1.amazoncognito.com';
  
  console.log('🔄 Using fallback Cognito configuration:', {
    userPoolId: userPoolId.substring(0, 10) + '...',
    userPoolClientId: userPoolClientId.substring(0, 10) + '...',
    cognitoDomain: cognitoDomain.substring(0, 30) + '...'
  });
}

// Determine redirect URLs dynamically based on current origin
const getCurrentOrigin = () => {
  if (typeof window !== 'undefined' && window.location) {
    return window.location.origin;
  }
  // Fallback for server-side rendering or when window is not available
  return process.env.NODE_ENV === 'production'
    ? 'https://app.yatabase.com'
    : 'https://dev-app.yatabase.com';
};

const currentOrigin = getCurrentOrigin();
console.log('🌐 Current origin detected:', currentOrigin);

// Build redirect URLs based on current origin
const getRedirectUrls = () => {
  const origin = getCurrentOrigin();

  // For known domains, use them directly
  const knownDomains = {
    'https://app.yatabase.com': {
      signIn: 'https://app.yatabase.com/auth/callback',
      signOut: 'https://app.yatabase.com/logout'
    },
    'https://nearly.yatabase.com': {
      signIn: 'https://nearly.yatabase.com/auth/callback',
      signOut: 'https://nearly.yatabase.com/logout'
    },
    'https://dev-app.yatabase.com': {
      signIn: 'https://dev-app.yatabase.com/auth/callback',
      signOut: 'https://dev-app.yatabase.com/logout'
    },
    'https://next.yatabase.com': {
      signIn: 'https://next.yatabase.com/auth/callback',
      signOut: 'https://next.yatabase.com/logout'
    },
    'http://localhost:3000': {
      signIn: 'http://localhost:3000/auth/callback',
      signOut: 'http://localhost:3000/logout'
    },
    'http://localhost:3001': {
      signIn: 'http://localhost:3001/auth/callback',
      signOut: 'http://localhost:3001/logout'
    }
  };

  // If current origin is known, use it
  if (knownDomains[origin]) {
    return {
      redirectSignIn: [knownDomains[origin].signIn],
      redirectSignOut: [knownDomains[origin].signOut]
    };
  }

  // Otherwise, build URLs dynamically from current origin
  console.warn('⚠️ Unknown origin detected, building redirect URLs dynamically:', origin);
  return {
    redirectSignIn: [`${origin}/auth/callback`],
    redirectSignOut: [`${origin}/logout`]
  };
};

const redirectUrls = getRedirectUrls();
console.log('🔄 Using redirect URLs:', redirectUrls);

// Cognito configuration from environment variables
const cognitoConfig = {
  Auth: {
    Cognito: {
      userPoolId: userPoolId,
      userPoolClientId: userPoolClientId,
      loginWith: {
        oauth: {
          domain: cognitoDomain?.replace('https://', ''),
          scopes: ['openid', 'email', 'profile'],
          redirectSignIn: redirectUrls.redirectSignIn,
          redirectSignOut: redirectUrls.redirectSignOut,
          responseType: 'code',
        },
        email: true,
        username: true, // Enable username = email
      },
      // Explicitly configure authentication flows to prevent 400 errors
      signUpVerificationMethod: 'code',
      userAttributes: {
        email: {
          required: true,
        },
      },
    }
  }
};

// Debug the final configuration object
console.log('🔍 Final Cognito configuration object:', {
  Auth: {
    Cognito: {
      userPoolId: cognitoConfig.Auth.Cognito.userPoolId,
      userPoolClientId: cognitoConfig.Auth.Cognito.userPoolClientId,
      domain: cognitoConfig.Auth.Cognito.loginWith.oauth.domain,
      redirectSignIn: cognitoConfig.Auth.Cognito.loginWith.oauth.redirectSignIn,
      redirectSignOut: cognitoConfig.Auth.Cognito.loginWith.oauth.redirectSignOut,
      currentOrigin: currentOrigin
    }
  }
});

// Configure Amplify
try {
  Amplify.configure(cognitoConfig);
  console.log('✅ Amplify configured successfully with Cognito');
} catch (error) {
  console.error('❌ Failed to configure Amplify:', error);
  throw error;
}

export default cognitoConfig; 